import type { DocTypeSchema } from "./meta";
import { getDocTitle } from "./docTitle";

/**
 * Doctype-agnostic summary of a SAVED document: name, title (meta.title_field),
 * docstatus/status and a few key fields chosen from meta — title_field, status,
 * then `in_list_view && !hidden` fields. Values are primitives, truncated.
 *
 * Used by the active-detail-page registry for page-aware AI (plan 1968). Pass the
 * saved document, never live form data with unsaved edits.
 */
export interface RecordSummary {
  name?: string;
  title?: string;
  isNew: boolean;
  docstatus?: number;
  status?: string;
  fields: Record<string, string | number | boolean>;
}

/** Field types whose values are not meaningful/safe as short key facts. */
export const SKIPPED_FIELDTYPES = new Set([
  "Table",
  "Table MultiSelect",
  "Section Break",
  "Column Break",
  "Tab Break",
  "HTML",
  "Fold",
  "Heading",
  "Button",
  "Password",
  "Attach",
  "Attach Image",
  "Image",
  "Signature",
  "Geolocation",
  "Code",
  "JSON",
  "Text Editor",
  "HTML Editor",
  "Markdown Editor",
]);

export const SECRET_FIELDNAME = /token|password|passwd|secret|api_?key|otp|credential/i;

export const RECORD_SUMMARY_DEFAULTS = { maxFields: 8, maxChars: 120 } as const;

function toPrimitive(value: unknown, maxChars: number): string | number | boolean | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const text = value.replace(/\s+/g, " ").trim();
    if (!text) return undefined;
    return text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text;
  }
  return undefined;
}

export function summarizeRecord(
  meta: Pick<DocTypeSchema, "fields" | "title_field"> | null | undefined,
  doc: Record<string, unknown> | null | undefined,
  options: { maxFields?: number; maxChars?: number } = {},
): RecordSummary {
  const maxFields = options.maxFields ?? RECORD_SUMMARY_DEFAULTS.maxFields;
  const maxChars = options.maxChars ?? RECORD_SUMMARY_DEFAULTS.maxChars;
  const name = typeof doc?.name === "string" && doc.name ? doc.name : undefined;
  if (!doc || !name || doc.__islocal) return { isNew: true, fields: {} };

  const summary: RecordSummary = { name, isNew: false, fields: {} };
  const title = getDocTitle(meta, doc);
  if (title && title !== name) summary.title = toPrimitive(title, maxChars) as string;
  if (typeof doc.docstatus === "number") summary.docstatus = doc.docstatus;
  const status = toPrimitive(doc.status, maxChars);
  if (typeof status === "string") summary.status = status;

  const byName = new Map((meta?.fields ?? []).map((f) => [f.fieldname, f]));
  const candidates: string[] = [];
  if (meta?.title_field) candidates.push(meta.title_field);
  if (byName.has("status")) candidates.push("status");
  for (const f of meta?.fields ?? []) {
    if (f.in_list_view && !f.hidden) candidates.push(f.fieldname);
  }

  for (const fieldname of candidates) {
    if (Object.keys(summary.fields).length >= maxFields) break;
    if (fieldname in summary.fields || SECRET_FIELDNAME.test(fieldname)) continue;
    const field = byName.get(fieldname);
    if (field && SKIPPED_FIELDTYPES.has(field.fieldtype)) continue;
    const value = toPrimitive(doc[fieldname], maxChars);
    if (value !== undefined) summary.fields[fieldname] = value;
  }

  return summary;
}
